<template>
  <UiInput
    v-bind="inputProps"
    v-model="maskedValue"
    :mask="mask"
    :rules="computedRules"
    ref="inputRef"
    @keydown="onKeydownHandler"
    @blur="onBlurHandler"
    @focus="onFocusHandler"
    @paste="onPaste"
    :errors="localErrors"
    :errors-manager="props.errorsManager"
    :field-name="props.fieldName"
    :required="props.required"
    :label="props.label"
    placeholder="+7"
  >
    <template
      v-for="(slot, name) in $slots"
      :key="name"
      v-slot:[name]="slotProps">
      <slot
        :name="name"
        v-bind="{
          ...(slotProps || {}),
          isValid,
          hasValue: !!localValue
        }"
      />
    </template>
  </UiInput>
</template>

<script setup lang="ts">
import checkPhone from '@/core/util/validate/checkPhone';
import { computed, ref, useAttrs, watch } from 'vue';
import UiInput, { UiInputProps } from './UiInput.vue';
import { QINPUT_PROPS_DEFAULT } from './hooks';

const props = withDefaults(defineProps<UiInputProps & {
  emitValid?: boolean
}>(), {
  ...QINPUT_PROPS_DEFAULT,
})


const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'keydown', 'paste']);

const attrs = useAttrs();

const filteredAttrs = computed(() => {
  const result: Record<string, any> = {};
  for (const key in attrs) {
    if (!/^on[A-Z]/.test(key)) {
      result[key] = attrs[key];
    }
  }
  return result;
});

const inputRef = ref<InstanceType<typeof UiInput> | null>(null);
const isTyping = ref(false);
const localErrors = ref<any[]>([]);

// Локальное значение для внутреннего состояния
const localValue = ref<string>('');

const mask = computed(() => props.mask || '+7 (###) ###-##-##');

// Единая функция проверки валидности номера
const validatePhone = (val: string): boolean => {
  if (!val) return false;

  let digits = val.replace(/\D/g, '');

  if (!digits || digits.length < 10) return false;

  return checkPhone(digits);
};

// Проверка валидности текущего значения
const isValid = computed(() => validatePhone(localValue.value));

// Синхронизация локального значения с входящим modelValue
watch(() => props.modelValue, (newValue) => {
  if (!newValue || newValue === '7') {
    localValue.value = '';
    return;
  }
  const digits = String(newValue).replace(/\D/g, '');
  if (digits.length < 2 || digits[0] !== '7') {
    localValue.value = '';
    return;
  }
  localValue.value = digits.slice(1);
}, { immediate: true });

const maskedValue = computed({
  get() {
    return localValue.value;
  },
  set(val: string) {
    val = val || '';
    localValue.value = val;

    let digits = val.replace(/\D/g, '');

    if (!digits) {
      emit('update:modelValue', '');
      return;
    }

    // Нормализация номера
    if (digits.length === 11 && digits[0] === '8') {
      digits = '7' + digits.slice(1);
    }
    if (digits[0] !== '7') {
      if (digits.length === 10) {
        digits = '7' + digits;
      }
    }

    // Проверка валидности перед эмиссией
    const isValidNumber = checkPhone(digits);

    if (props.emitValid) {
      // Эмитим только если валидно
      if (isValidNumber) {
        emit('update:modelValue', digits);
      }
    } else {
      // Эмитим всегда
      emit('update:modelValue', digits);
    }
  }
});

const computedRules = computed(() => [
  (val: string) => {
    if (isTyping.value && val && val.replace(/\D/g, '').length < 10) return true;
    if (!val) return true;
    if (!validatePhone(val)) return 'Неверный формат телефона';
    return true;
  },
  ...(props.rules || [])
]);

function onKeydownHandler(e: KeyboardEvent) {
  isTyping.value = true;
  localErrors.value = [];
  inputRef.value?.resetValidation?.();
  
  // Проверяем, является ли это вводом цифры 7 или 8
  const key = e.key;
  
  // Если поле пустое и пользователь вводит 7 или 8 - отменяем ввод
  if ((key === '7' || key === '8') && !localValue.value) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  
  emit('keydown', e);
}

function onBlurHandler(e: Event) {
  isTyping.value = false;
  (inputRef.value as any)?.validate?.();
  emit('blur', e);
}

function onFocusHandler(e: Event) {
  emit('focus', e);
}

const onPaste = (e: ClipboardEvent) => {
  const clipboardData = e.clipboardData || (window as any).clipboardData;
  if (!clipboardData) return;
  const pasted = clipboardData.getData('Text');
  let digits = pasted.replace(/\D/g, '');

  if (digits.length === 11 && digits[0] === '8') {
    digits = '7' + digits.slice(1);
  }
  if (digits.length === 11 && digits[0] === '7') {
    digits = digits.slice(1);
  }
  if (digits.length === 10) {
    maskedValue.value = digits;
    e.preventDefault();
  }
};

const inputProps = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {modelValue, ...rest} = props;

  const _props = {
    ...rest,
    mask: mask.value,
    rules: computedRules.value,
  };

  delete _props['onUpdate:modelValue']
  return _props
});

defineExpose({
  inputRef,
  clearExternalErrors: () => {
    inputRef.value?.resetValidation?.();
    localErrors.value = [];
  }
});
</script>
